import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Pagination,
  Grid,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Stack,
  MenuItem,
  Card,
  Alert,
  Tooltip,
  CircularProgress,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SortIcon from "@mui/icons-material/Sort";
import CategoryIcon from "@mui/icons-material/Category";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import TableChartIcon from "@mui/icons-material/TableChart";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import DownloadIcon from "@mui/icons-material/Download";
import WarningIcon from "@mui/icons-material/Warning";
import GavelRoundedIcon from "@mui/icons-material/GavelRounded";

import { useParams, useLocation, Outlet } from "react-router-dom";

import api from "../../utils/api";
import { fetchViolationStatuses } from "./violations.api";
import { getViolationStatusDetails } from "./violation.utils";

import ViolationStatusChips from "./ViolationStatusCards";
import ViolationCards from "./ViolationCards";
import ViolationTable from "./ViolationTable";

/* ------------------------------------------------------------------ */
/* Shared styles                                                       */
/* ------------------------------------------------------------------ */
const pageCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const secondaryButtonSx = {
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
  px: 2,
  borderColor: "rgba(47,99,173,0.18)",
  color: "#223142",
  whiteSpace: "nowrap",
  "&:hover": {
    borderColor: "#2f63ad",
    color: "#2f63ad",
    backgroundColor: "rgba(47,99,173,0.04)",
  },
};

const filterFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "rgba(255,255,255,0.96)",
  },
};

/* ------------------------------------------------------------------ */
/* Neighborhood labels & helpers                                       */
/* ------------------------------------------------------------------ */
const GROUP_NAME_BY_CODE = {
  "EH CH HILL": "Estate Homes",
  "EH EDGMT": "Estate Homes",
  SV: "Sommerset Villas",
  SC: "Southmont Cove",
  SW: "Sutton Walk",
  "VILLA HMS": "Villa Homes",
  WF: "Waterford",
  WW: "Wedgewood",
};

const norm = (v) => (v ?? "").toString().trim();
const canon = (v) => norm(v).replace(/\s+/g, " ").toUpperCase();
const isRenter = (v) => /\bRENTER\b/i.test(v);

const groupNameFor = (raw) => {
  const c = canon(raw);
  return GROUP_NAME_BY_CODE[c] || norm(raw);
};

const groupIdFor = (raw) => canon(groupNameFor(raw));

/* ------------------------------------------------------------------ */
/* Filter Bar                                                          */
/* ------------------------------------------------------------------ */
const FilterBar = ({
  viewMode,
  setViewMode,
  searchTerm,
  setSearchTerm,
  filter,
  setFilter,
  sortOrder,
  setSortOrder,
  selectedType,
  setSelectedType,
  selectedNeighborhood,
  setSelectedNeighborhood,
  violationTypes,
  neighborhoodOptions,
  onReset,
  totalCount,
  title = "Violations",
}) => {
  const hasActive =
    !!searchTerm ||
    !!filter.startDate ||
    !!filter.endDate ||
    !!selectedType ||
    !!selectedNeighborhood;

  const getTypeLabel = (id) =>
    violationTypes.find((t) => String(t.id) === String(id))?.name || id;

  const dateLabel =
    filter.startDate || filter.endDate
      ? `${filter.startDate || "…"}. ${filter.endDate || "…"}`
      : "";

  const neighborhoodLabel =
    selectedNeighborhood &&
    (neighborhoodOptions.find((n) => n.code === selectedNeighborhood)?.label ||
      selectedNeighborhood);

  return (
    <Card sx={{ ...pageCardSx, overflow: "hidden", mb: 3 }}>
      <Box
        sx={{
          px: { xs: 1.5, sm: 2, md: 3 },
          pt: 2.25,
          pb: 1.5,
          borderBottom: "1px solid rgba(47,99,173,0.08)",
          backgroundColor: "rgba(47,99,173,0.02)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <GavelRoundedIcon sx={{ color: "#2f63ad" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
                {title}
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              Search, filter, and review community violations.
            </Typography>
          </Box>

          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            sx={{
              borderRadius: "12px",
              overflow: "hidden",
              border: "1px solid rgba(47,99,173,0.08)",
              "& .MuiToggleButton-root": {
                px: 1.75,
                border: "none",
                textTransform: "none",
                fontWeight: 600,
                color: "#5f6b7a",
              },
              "& .Mui-selected": {
                color: "#2f63ad !important",
                backgroundColor: "rgba(47,99,173,0.08)",
              },
            }}
          >
            <ToggleButton value="cards">
              <ViewModuleIcon sx={{ mr: 1 }} /> Cards
            </ToggleButton>
            <ToggleButton value="table">
              <TableChartIcon sx={{ mr: 1 }} /> Table
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              value={searchTerm}
              placeholder="Search by violation #, address, type, status, or property..."
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && setSearchTerm("")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={filterFieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Start"
              size="small"
              value={filter.startDate}
              onChange={(e) =>
                setFilter((f) => ({ ...f, startDate: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarMonthIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={filterFieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="End"
              size="small"
              value={filter.endDate}
              onChange={(e) =>
                setFilter((f) => ({ ...f, endDate: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarMonthIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={filterFieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="Sort by"
              size="small"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              sx={filterFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SortIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="dateDesc">Newest to Oldest</MenuItem>
              <MenuItem value="dateAsc">Oldest to Newest</MenuItem>
              <MenuItem value="idAsc">Violation ID Ascending</MenuItem>
              <MenuItem value="idDesc">Violation ID Descending</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="Violation Type"
              size="small"
              value={selectedType}
              onChange={(e) => setSelectedType(String(e.target.value))}
              sx={filterFieldSx}
              SelectProps={{ displayEmpty: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CategoryIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="">
                <em>All Types</em>
              </MenuItem>
              {violationTypes
                .slice()
                .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                .map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name || t.id}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="Neighborhood"
              size="small"
              value={selectedNeighborhood}
              onChange={(e) => setSelectedNeighborhood(e.target.value)}
              sx={filterFieldSx}
              SelectProps={{ displayEmpty: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="">
                <em>All Neighborhoods</em>
              </MenuItem>
              {neighborhoodOptions.map(({ code, label }) => (
                <MenuItem key={code} value={code}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                gap: 1.5,
                flexDirection: { xs: "column", sm: "row" },
                mt: 0.5,
              }}
            >
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {searchTerm && (
                  <Chip
                    label={`Search: ${searchTerm}`}
                    onDelete={() => setSearchTerm("")}
                    size="small"
                    variant="outlined"
                  />
                )}
                {dateLabel && (
                  <Chip
                    label={`Date: ${dateLabel}`}
                    onDelete={() => setFilter({ startDate: "", endDate: "" })}
                    size="small"
                    variant="outlined"
                  />
                )}
                {selectedType && (
                  <Chip
                    label={`Type: ${getTypeLabel(selectedType)}`}
                    onDelete={() => setSelectedType("")}
                    size="small"
                    variant="outlined"
                  />
                )}
                {selectedNeighborhood && (
                  <Chip
                    label={`Neighborhood: ${neighborhoodLabel}`}
                    onDelete={() => setSelectedNeighborhood("")}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  {totalCount} result{totalCount === 1 ? "" : "s"}
                </Typography>

                {hasActive && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ClearRoundedIcon />}
                    onClick={onReset}
                    sx={secondaryButtonSx}
                  >
                    Reset
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

/* ------------------------------------------------------------------ */
/* Main Page                                                           */
/* ------------------------------------------------------------------ */
// Mirrors the "overdue" definition used by the dashboard's Needs Your Action
// summary (routes/dashboard.js): active status, past the violation type's
// cure window.
const OVERDUE_EXCLUDED_STATUSES = ["cancelled", "violation cured", "void"];

const ViolationsPage = () => {
  const { id } = useParams();
  const location = useLocation();

  const [violations, setViolations] = useState([]);
  const [filteredViolations, setFilteredViolations] = useState([]);
  const [violationTypes, setViolationTypes] = useState([]);
  const [statusMeta, setStatusMeta] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});

  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("table");
  const [sortBy, setSortBy] = useState("dateDesc");
  const [filter, setFilter] = useState({ startDate: "", endDate: "" });
  const [selectedType, setSelectedType] = useState("");
  const [selectedNeighborhood, setSelectedNeighborhood] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(
    () => new URLSearchParams(location.search).get("overdue") === "1"
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [slaBreached, setSlaBreached] = useState(0);
  const [exporting, setExporting] = useState(false);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token") || "";
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      if (selectedType) params.set("type_id", selectedType);
      const res = await fetch(`${window.location.origin}/api/violations/export/csv?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href=url; a.download=`violations-${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { console.error("Export failed:", err); }
    finally { setExporting(false); }
  };
  const itemsPerPage = 50;

  const neighborhoodOptions = useMemo(() => {
    const map = new Map();
    for (const v of violations || []) {
      const raw = norm(v?.ClubMembershipTypeCode);
      if (!raw || isRenter(raw)) continue;
      const idCode = groupIdFor(raw);
      const label = groupNameFor(raw);
      map.set(idCode, { code: idCode, label });
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [violations]);

  useEffect(() => {
    if (!id) {
      api.get(`/getViolations`).then((res) => {
        const data = res.data || [];
        setViolations(data);
        setFilteredViolations(sortViolations(data, sortBy));
        calculateStatusCounts(data);
      });
    }
  }, [id]);

  useEffect(() => {
    api.get(`/getCcrViolationTypes`).then((res) => {
      const list = (res.data || []).map((t) => ({
        id: String(t?.id ?? t?.type_id),
        name: t?.title || "",
        rule: t?.rule || "",
        days_to_cure: t?.days_to_cure ?? null,
      }));
      setViolationTypes(list);
    });
  }, []);

  useEffect(() => {
    fetchViolationStatuses()
      .then((res) => setStatusMeta(res.data))
      .catch((err) => console.error("Failed to load status metadata:", err));
  }, []);

  useEffect(() => {
    applyFilters();
  }, [
    searchTerm,
    filterStatus,
    filter,
    selectedType,
    selectedNeighborhood,
    overdueOnly,
    violations,
    violationTypes,
    sortBy,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filter, selectedType, selectedNeighborhood, overdueOnly, sortBy]);

  const sortViolations = (data, criteria) => {
    return [...data].sort((a, b) => {
      const dateA = new Date(a.violated_at);
      const dateB = new Date(b.violated_at);

      switch (criteria) {
        case "dateAsc":
          return dateA - dateB;
        case "dateDesc":
          return dateB - dateA;
        case "idAsc":
          return Number(a.id) - Number(b.id);
        case "idDesc":
          return Number(b.id) - Number(a.id);
        default:
          return 0;
      }
    });
  };

  const calculateStatusCounts = (data) => {
    const counts = data.reduce((acc, v) => {
      const key = String(v.status || "").toLowerCase();
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    setStatusCounts(counts);
  };

  const filteredStatusCounts = useMemo(() => {
    const counts = {};
    for (const v of filteredViolations) {
      const key = String(v.status || "").toLowerCase();
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [filteredViolations]);

  const getTypeName = (typeId) =>
    violationTypes.find((t) => String(t.id) === String(typeId))?.name || "";

  const applyFilters = () => {
    let filtered = [...violations];

    const terms = searchTerm
      .toLowerCase()
      .split(/[\s,]+/)
      .map((t) => t.trim())
      .filter(Boolean);

    if (terms.length) {
      filtered = filtered.filter((v) => {
        const searchableFields = [
          `vlt-${v.id}`,
          v.id,
          v.property_id,
          v.account_id,
          v.status,
          v.resolution,
          v.comment,
          v.member?.fullname,
          v.member?.full_address,
          v.member?.ClubMembershipTypeCode,
          v.cure_by_user?.name,
          getTypeName(v.ccr_violation_type_id),
          v.violated_at,
        ];

        const combined = searchableFields
          .map((x) => (x ?? "").toString().toLowerCase().trim())
          .join(" ");

        return terms.some((term) =>
          combined.split(/\s+/).some((word) => word.includes(term))
        );
      });
    }

    if (filterStatus) {
      const s = filterStatus.toLowerCase();
      filtered = filtered.filter((v) => String(v.status || "").toLowerCase() === s);
    }

    if (filter.startDate || filter.endDate) {
      filtered = filtered.filter((v) => {
        const date = String(v.violated_at || "").slice(0, 10);
        return (
          (!filter.startDate || date >= filter.startDate) &&
          (!filter.endDate || date <= filter.endDate)
        );
      });
    }

    if (selectedType) {
      filtered = filtered.filter(
        (v) => String(v.ccr_violation_type_id) === String(selectedType)
      );
    }

    if (selectedNeighborhood) {
      filtered = filtered.filter(
        (v) => groupIdFor(v?.ClubMembershipTypeCode) === selectedNeighborhood
      );
    }

    if (overdueOnly) {
      filtered = filtered.filter((v) => {
        if (OVERDUE_EXCLUDED_STATUSES.includes(String(v.status || "").toLowerCase())) return false;
        const type = violationTypes.find((t) => String(t.id) === String(v.ccr_violation_type_id));
        const daysToCure = type?.days_to_cure;
        if (daysToCure == null || !v.violated_at) return false;
        const daysSince = (Date.now() - new Date(v.violated_at).getTime()) / (1000 * 60 * 60 * 24);
        return daysSince > daysToCure;
      });
    }

    setFilteredViolations(sortViolations(filtered, sortBy));
  };

  const handleStatusFilter = (status) => {
    setFilterStatus((prev) => (prev === status ? "" : status));
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setFilterStatus("");
    setFilter({ startDate: "", endDate: "" });
    setSelectedType("");
    setSelectedNeighborhood("");
    setOverdueOnly(false);
    setSortBy("dateDesc");
  };

  const paginated = filteredViolations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Load SLA breach count
  // eslint-disable-next-line react-hooks/rules-of-hooks
  React.useEffect(() => {
    api.get("/violations/sla-status").then(r => {
      setSlaBreached((r.data||[]).filter(x=>x.sla_status==="breached").length);
    }).catch(()=>{});
  }, []);

  if (id) return <Outlet />;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: "1200px", mx: "auto" }}>

      {/* SLA breach banner */}
      {slaBreached > 0 && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb:2, borderRadius:"12px" }}>
          <strong>{slaBreached} violation{slaBreached!==1?"s":""}</strong> have exceeded their SLA target and need attention.
        </Alert>
      )}

      {/* Export button */}
      <Box sx={{ display:"flex", justifyContent: overdueOnly ? "space-between" : "flex-end", alignItems:"center", mb:1.5 }}>
        {overdueOnly && (
          <Chip
            label="Showing: Overdue violations"
            onDelete={() => setOverdueOnly(false)}
            sx={{ borderRadius:"8px", fontWeight:600, backgroundColor:"rgba(220,38,38,0.1)", color:"#dc2626" }}
          />
        )}
        <Tooltip title="Export current view to CSV">
          <Button size="small" variant="outlined" startIcon={<DownloadIcon fontSize="small" />}
            onClick={handleExportCSV} disabled={exporting}
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600, borderColor:"rgba(47,99,173,0.3)", color:"#2f63ad" }}>
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
        </Tooltip>
      </Box>

      <FilterBar
        viewMode={viewMode}
        setViewMode={setViewMode}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filter={filter}
        setFilter={setFilter}
        sortOrder={sortBy}
        setSortOrder={setSortBy}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        selectedNeighborhood={selectedNeighborhood}
        setSelectedNeighborhood={setSelectedNeighborhood}
        violationTypes={violationTypes}
        neighborhoodOptions={neighborhoodOptions}
        onReset={handleResetFilters}
        totalCount={filteredViolations.length}
        title="Violations"
      />

      <Box sx={{ mb: 3 }}>
        <ViolationStatusChips
          statuses={statusMeta}
          statusCounts={filteredStatusCounts}
          activeFilters={filterStatus ? [filterStatus] : []}
          onToggle={handleStatusFilter}
        />
      </Box>

      {viewMode === "table" ? (
        <ViolationTable
          violations={filteredViolations}
          violationTypes={violationTypes}
          statusMeta={statusMeta}
        />
      ) : (
        <>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              Card View
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
              Showing {filteredViolations.length} violation
              {filteredViolations.length === 1 ? "" : "s"}.
            </Typography>
          </Box>

          <ViolationCards
            violations={paginated}
            violationTypes={violationTypes}
            statuses={statusMeta}
            getStatusDetails={getViolationStatusDetails}
          />

          {filteredViolations.length > itemsPerPage && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Pagination
                count={Math.ceil(filteredViolations.length / itemsPerPage)}
                page={currentPage}
                onChange={(_, value) => setCurrentPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ViolationsPage;
